import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import {
  getAttachmentLabel,
  isImageUrl,
  normalizeAttachmentList
} from '../../../helpers/paymentAttachments'
import { getCurrentDate } from '../../../helpers/formatDate'
import { formatQuantity, hasExportValue, sanitizeFilenamePart } from '../../../helpers/utils'
import { UI_TEXT_NA } from '../../../constants/uiText'
import { REFUND_APPROVAL_TEXT } from '../../constants/refundApprovalText'
import { loadImageDataUrl, renderPdfToImageDataUrls } from '../../../settlements/helpers/pdfBuilder'

const COLORS = {
  primary: [80, 115, 154],
  primaryDark: [52, 68, 91],
  tableHeader: [158, 154, 154],
  tableBorder: [213, 216, 220],
  detailsBorder: [205, 208, 212],
  metricCardBg: [250, 251, 252],
  metricCardBorder: [200, 200, 200],
  metricCardTitle: [130, 130, 130],
  metricCardText: [33, 33, 33],
  border: [206, 213, 223],
  softBorder: [222, 226, 232],
  softBg: [246, 248, 251],
  text: [32, 37, 45],
  muted: [112, 124, 141],
  white: [255, 255, 255],
  danger: [199, 51, 45],
  dangerBg: [255, 226, 224],
  warning: [151, 103, 6],
  warningBg: [255, 239, 165],
  success: [0, 116, 70],
  successBg: [194, 234, 214],
  neutralBg: [231, 236, 243]
}

const PAGE_MARGIN_X = 28
const PAGE_TOP_Y = 34
const FOOTER_TOP_OFFSET = 36
const FOOTER_TEXT_OFFSET = 22
const CONTENT_WIDTH = 792 - PAGE_MARGIN_X * 2

const firstPresentValue = (...values) => {
  const value = values.find(
    (current) => current !== null && current !== undefined && String(current).trim() !== ''
  )

  return value === undefined ? UI_TEXT_NA : String(value)
}

const normalizePdfText = (value, fallback = UI_TEXT_NA) => {
  if (value === null || value === undefined || value === '') return fallback
  return String(value)
}

const getPageWidth = (doc) => doc.internal.pageSize.getWidth()
const getPageHeight = (doc) => doc.internal.pageSize.getHeight()
const getPageRight = (doc) => getPageWidth(doc) - PAGE_MARGIN_X
const getContentBottomY = (doc) => getPageHeight(doc) - FOOTER_TOP_OFFSET - 16

const setTextColor = (doc, color) => doc.setTextColor(color[0], color[1], color[2])
const setDrawColor = (doc, color) => doc.setDrawColor(color[0], color[1], color[2])
const setFillColor = (doc, color) => doc.setFillColor(color[0], color[1], color[2])

const isFetchableUrl = (url = '') => /^(https?:|data:)/i.test(String(url).trim())
const isUnavailableLocalUrl = (url = '') => /^(content|file):\/\//i.test(String(url).trim())
const getUrlPath = (url = '') => String(url).split(/[?#]/, 1)[0].toLowerCase()
const isPdfUrl = (url = '', filename = '') =>
  getUrlPath(url).endsWith('.pdf') ||
  String(filename || '')
    .toLowerCase()
    .endsWith('.pdf') ||
  /^data:application\/pdf/i.test(String(url || ''))

const getStatusColors = (status = '') => {
  const normalized = String(status || '').toLowerCase()

  if (
    normalized.includes('proces') ||
    normalized.includes('verific') ||
    normalized.includes('recib')
  ) {
    return { bg: COLORS.successBg, text: COLORS.success }
  }

  if (normalized.includes('rechaz') || normalized.includes('anulad')) {
    return { bg: COLORS.dangerBg, text: COLORS.danger }
  }

  if (normalized.includes('parcial')) {
    return { bg: COLORS.warningBg, text: COLORS.warning }
  }

  return { bg: COLORS.neutralBg, text: COLORS.primaryDark }
}

const drawStatusPill = (doc, text, cell) => {
  const label = normalizePdfText(text, '').trim()
  if (!label) return

  const { bg, text: textColor } = getStatusColors(label)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(7.5)
  const pillHeight = Math.min(15, Math.max(cell.height - 7, 11))
  const pillWidth = Math.max(10, Math.min(cell.width - 8, doc.getTextWidth(label) + 20))
  const pillX = cell.x + (cell.width - pillWidth) / 2
  const pillY = cell.y + (cell.height - pillHeight) / 2

  setFillColor(doc, bg)
  doc.roundedRect(pillX, pillY, pillWidth, pillHeight, pillHeight / 2, pillHeight / 2, 'F')

  setTextColor(doc, textColor)
  doc.text(label, cell.x + cell.width / 2, pillY + pillHeight / 2 + 2.5, {
    align: 'center'
  })
}

const fitInside = ({ width, height }, maxWidth, maxHeight) => {
  const safeWidth = Number(width) || maxWidth
  const safeHeight = Number(height) || maxHeight
  const scale = Math.min(maxWidth / safeWidth, maxHeight / safeHeight)

  return {
    width: safeWidth * scale,
    height: safeHeight * scale
  }
}

const parseValidDate = (date) => {
  if (!date) return null

  const parsedDate = new Date(date)
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate
}

const formatHeaderDateTime = (date) => {
  const parsedDate = parseValidDate(date)
  if (!parsedDate) return UI_TEXT_NA

  const formatter = new Intl.DateTimeFormat('es', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  })

  return formatter
    .format(parsedDate)
    .replace(/\s*a\.\s*m\./i, ' a.m.')
    .replace(/\s*p\.\s*m\./i, ' p.m.')
}

const drawHeaderInlineLabel = (doc, label, value, x, y, gap = 4) => {
  doc.setFont('helvetica', 'bold')
  const labelWidth = doc.getTextWidth(label)
  doc.text(label, x, y)
  doc.setFont('helvetica', 'normal')
  doc.text(normalizePdfText(value).trim(), x + labelWidth + gap, y)
}

const drawHeaderRightInlineLabel = (doc, label, value, rightX, y) => {
  doc.setFont('helvetica', 'bold')
  const labelWidth = doc.getTextWidth(label)
  doc.setFont('helvetica', 'normal')
  const valueWidth = doc.getTextWidth(normalizePdfText(value).trim())
  const gap = 4
  const startX = rightX - labelWidth - valueWidth - gap

  drawHeaderInlineLabel(doc, label, value, startX, y, gap)
}

const drawWrappedHeaderText = (doc, text, x, y, maxWidth, lineHeight = 12) => {
  const lines = doc.splitTextToSize(normalizePdfText(text).trim(), maxWidth)
  doc.text(lines, x, y)
  return y + Math.max(lines.length, 1) * lineHeight
}

const drawFooterOnPage = (doc, pageNumber, totalPages, printDate) => {
  const pageHeight = getPageHeight(doc)
  const right = getPageRight(doc)
  const lineY = pageHeight - FOOTER_TOP_OFFSET
  const textY = pageHeight - FOOTER_TEXT_OFFSET

  setDrawColor(doc, COLORS.softBorder)
  doc.setLineWidth(0.6)
  doc.line(PAGE_MARGIN_X, lineY, right, lineY)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7.5)
  setTextColor(doc, COLORS.muted)
  doc.text(`Generado: ${printDate}`, PAGE_MARGIN_X, textY)
  doc.text(`Página ${pageNumber} de ${totalPages}`, right, textY, { align: 'right' })
  setTextColor(doc, COLORS.text)
}

const addPageFooters = (doc, printDate) => {
  const totalPages = doc.internal.getNumberOfPages()

  for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
    doc.setPage(pageNumber)
    drawFooterOnPage(doc, pageNumber, totalPages, printDate)
  }
}

const ensureSectionStart = (doc, startY, minHeight = 90) => {
  if (startY + minHeight <= getContentBottomY(doc)) return startY

  doc.addPage()
  return PAGE_TOP_Y
}

const drawSectionTitle = (doc, title, x, y, subtitle = '') => {
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10)
  setTextColor(doc, COLORS.text)
  doc.text(title, x, y)

  if (subtitle) {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7.5)
    setTextColor(doc, COLORS.muted)
    doc.text(subtitle, x, y + 12)
  }
}

const drawReportHeader = (doc, activity) => {
  const refundRef = firstPresentValue(activity?.refund_db_ref, activity?.uuid)
  const right = getPageRight(doc)
  const business = activity?.business || {}
  const businessName = firstPresentValue(
    business.business_description,
    business.description,
    business.business_name,
    business.commercial_name,
    business.legal_name,
    business.name
  )
  const businessRuc = firstPresentValue(
    business.ruc,
    business.nit,
    business.tax_id,
    business.taxId,
    business.business_ruc
  )
  const businessAddress = firstPresentValue(
    business.address,
    business.business_address,
    business.location,
    business.direction
  )
  const businessPhone = firstPresentValue(
    business.phone_number,
    business.phone,
    business.telephone,
    business.mobile,
    business.business_phone
  )
  const userCode = firstPresentValue(
    activity?.salesman_db_ref,
    activity?.salesman_uid,
    activity?.user_uid
  )
  const userName = firstPresentValue(
    activity?.salesman_name,
    activity?.salesman_full_name,
    activity?.user_full_name
  )
  const createdAt = firstPresentValue(
    activity?.display_created_date,
    activity?.created_date,
    activity?.created_at,
    activity?.collected_at
  )

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  setTextColor(doc, COLORS.text)
  let leftY = 38
  leftY = drawWrappedHeaderText(doc, businessName, PAGE_MARGIN_X, leftY, 430)
  leftY = drawWrappedHeaderText(doc, `RUC: ${businessRuc}`, PAGE_MARGIN_X, leftY, 430)
  leftY = drawWrappedHeaderText(doc, `Dirección: ${businessAddress}`, PAGE_MARGIN_X, leftY, 430)
  leftY = drawWrappedHeaderText(doc, `Teléfono: ${businessPhone}`, PAGE_MARGIN_X, leftY, 430)
  drawHeaderInlineLabel(doc, 'Usuario:', `${userCode} - ${userName}`, PAGE_MARGIN_X, leftY)

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  setTextColor(doc, COLORS.text)
  doc.text('Devolución', right, 42, { align: 'right' })

  doc.setFontSize(9)
  drawHeaderRightInlineLabel(doc, 'N° de Devolución:', refundRef, right, 58)
  drawHeaderRightInlineLabel(doc, 'N° NCR:', activity?.ncr_number || '-', right, 70)
  drawHeaderRightInlineLabel(doc, 'Fecha de creación:', formatHeaderDateTime(createdAt), right, 82)

  setDrawColor(doc, COLORS.softBorder)
  doc.setLineWidth(0.6)
  const dividerY = Math.max(leftY + 18, 104)
  doc.line(PAGE_MARGIN_X, dividerY, right, dividerY)

  return dividerY + 24
}

const drawMetricIcon = (doc, icon, x, y) => {
  setDrawColor(doc, COLORS.metricCardTitle)
  doc.setLineWidth(1)

  if (icon === 'products') {
    doc.line(x + 5, y, x + 10, y + 3)
    doc.line(x + 10, y + 3, x + 10, y + 10)
    doc.line(x + 10, y + 10, x + 5, y + 13)
    doc.line(x + 5, y + 13, x, y + 10)
    doc.line(x, y + 10, x, y + 3)
    doc.line(x, y + 3, x + 5, y)
    doc.line(x, y + 3, x + 5, y + 6)
    doc.line(x + 10, y + 3, x + 5, y + 6)
    doc.line(x + 5, y + 6, x + 5, y + 13)
    return
  }

  doc.roundedRect(x, y + 2, 12, 11, 1, 1, 'S')
  doc.line(x + 3, y + 5, x + 9, y + 5)
  doc.line(x + 3, y + 8, x + 9, y + 8)
}

const drawMetricCard = (doc, x, y, width, height, { icon, title, subtitle, value, footer }) => {
  const radius = 2

  setFillColor(doc, COLORS.metricCardBg)
  doc.roundedRect(x, y, width, height, radius, radius, 'F')
  setDrawColor(doc, COLORS.metricCardBorder)
  doc.setLineWidth(0.7)
  doc.roundedRect(x, y, width, height, radius, radius, 'S')

  drawMetricIcon(doc, icon, x + 10, y + 13)

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8)
  setTextColor(doc, COLORS.metricCardText)
  doc.text(title, x + 28, y + 23)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7.5)
  setTextColor(doc, COLORS.metricCardTitle)
  doc.text(subtitle, x + 10, y + 40)

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(18)
  setTextColor(doc, COLORS.metricCardText)
  doc.text(String(value), x + 10, y + 61)

  doc.setFontSize(7.5)
  setTextColor(doc, COLORS.metricCardText)
  doc.text(String(footer), x + 10, y + 78)
}

const drawSummaryCards = (doc, progress, y) => {
  drawSectionTitle(doc, 'RESUMEN DE DEVOLUCIÓN', PAGE_MARGIN_X, y)

  const cardY = y + 14
  const gap = 8
  const cardW = (CONTENT_WIDTH - gap) / 2
  const cardH = 92
  const pendingLines = Math.max(
    (Number(progress?.total_lines) || 0) - (Number(progress?.verified_lines) || 0),
    0
  )

  drawMetricCard(doc, PAGE_MARGIN_X, cardY, cardW, cardH, {
    icon: 'products',
    title: REFUND_APPROVAL_TEXT.productsMetricTitle,
    subtitle: REFUND_APPROVAL_TEXT.productsMetricSubtitle,
    value: `${formatQuantity(progress?.verified_lines)}/${formatQuantity(progress?.total_lines)}`,
    footer: `${REFUND_APPROVAL_TEXT.pendingMetricLabel}: ${formatQuantity(pendingLines)}`
  })
  drawMetricCard(doc, PAGE_MARGIN_X + (cardW + gap), cardY, cardW, cardH, {
    icon: 'quantity',
    title: REFUND_APPROVAL_TEXT.quantityMetricTitle,
    subtitle: REFUND_APPROVAL_TEXT.quantityMetricSubtitle,
    value: `${formatQuantity(progress?.verified_quantity)}/${formatQuantity(progress?.total_quantity)}`,
    footer: `${REFUND_APPROVAL_TEXT.rejectedMetricLabel}: ${formatQuantity(progress?.rejected_quantity)}`
  })

  return cardY + cardH
}

const buildDetailsRows = (summaryFields = [], extraFields = []) => {
  const fields = [...summaryFields, ...extraFields].filter(
    (field) => field?.label && hasExportValue(field.value)
  )
  const rows = []

  for (let index = 0; index < fields.length; index += 3) {
    const rowFields = [fields[index], fields[index + 1], fields[index + 2]]

    rows.push(
      rowFields.flatMap((field) =>
        field ? [normalizePdfText(field.label, ''), normalizePdfText(field.value, '-')] : ['', '']
      )
    )
  }

  return rows
}

const drawDetailsTable = (doc, startY, summaryFields, extraFields, activity) => {
  const rows = buildDetailsRows(summaryFields, extraFields)
  rows.splice(1, 0, [
    'Código del cliente',
    firstPresentValue(activity?.client_db_ref),
    'Nombre del cliente',
    firstPresentValue(activity?.client_name),
    '',
    ''
  ])

  const y = ensureSectionStart(doc, startY, 90)
  drawSectionTitle(doc, 'DETALLES DE DEVOLUCIÓN', PAGE_MARGIN_X, y)

  const labelWidth = 82
  const valueWidth = (CONTENT_WIDTH - labelWidth * 3) / 3

  autoTable(doc, {
    body: rows,
    startY: y + 12,
    theme: 'grid',
    margin: { left: PAGE_MARGIN_X, right: PAGE_MARGIN_X, bottom: 48 },
    styles: {
      font: 'helvetica',
      fontSize: 7.5,
      cellPadding: 4,
      overflow: 'linebreak',
      fillColor: COLORS.white,
      lineColor: COLORS.detailsBorder,
      lineWidth: 0.8,
      valign: 'middle'
    },
    alternateRowStyles: {
      fillColor: COLORS.white
    },
    columnStyles: {
      0: {
        cellWidth: labelWidth,
        fontStyle: 'bold',
        fontSize: 8.5,
        textColor: COLORS.text,
        fillColor: COLORS.white
      },
      1: { cellWidth: valueWidth, textColor: COLORS.text, fillColor: COLORS.white },
      2: {
        cellWidth: labelWidth,
        fontStyle: 'bold',
        fontSize: 8.5,
        textColor: COLORS.text,
        fillColor: COLORS.white
      },
      3: { cellWidth: valueWidth, textColor: COLORS.text, fillColor: COLORS.white },
      4: {
        cellWidth: labelWidth,
        fontStyle: 'bold',
        fontSize: 8.5,
        textColor: COLORS.text,
        fillColor: COLORS.white
      },
      5: { cellWidth: valueWidth, textColor: COLORS.text, fillColor: COLORS.white }
    }
  })

  return doc.lastAutoTable.finalY
}

const drawProductsTable = (doc, startY, tableHeaders, tableRows) => {
  const y = ensureSectionStart(doc, startY, 120)

  drawSectionTitle(doc, 'PRODUCTOS EN DEVOLUCIÓN', PAGE_MARGIN_X, y)

  autoTable(doc, {
    head: [tableHeaders],
    body: tableRows,
    startY: y + 14,
    theme: 'grid',
    margin: { left: PAGE_MARGIN_X, right: PAGE_MARGIN_X, bottom: 48 },
    styles: {
      font: 'helvetica',
      fontSize: 8,
      cellPadding: 3,
      textColor: COLORS.text,
      fillColor: COLORS.white,
      lineColor: COLORS.tableBorder,
      lineWidth: 0.7,
      minCellHeight: 18.5,
      overflow: 'linebreak',
      valign: 'middle'
    },
    headStyles: {
      fillColor: COLORS.tableHeader,
      textColor: COLORS.white,
      fontStyle: 'bold',
      font: 'helvetica',
      fontSize: 9,
      halign: 'center',
      valign: 'middle',
      lineColor: COLORS.tableBorder,
      lineWidth: 0.7,
      minCellHeight: 18.5
    },
    columnStyles: {
      0: { cellWidth: 48 },
      1: { cellWidth: 148 },
      2: { cellWidth: 86 },
      3: { cellWidth: 58 },
      4: { cellWidth: 54 },
      5: { cellWidth: 62 },
      6: { cellWidth: 55, halign: 'right' },
      7: { cellWidth: 55, halign: 'right' },
      8: { cellWidth: 52, halign: 'right' },
      9: { cellWidth: 52, halign: 'right' },
      10: { cellWidth: 66, halign: 'center', fontStyle: 'bold' }
    },
    didParseCell: ({ cell, column, section }) => {
      if (section !== 'body' || column.index !== tableHeaders.length - 1) return

      cell.styles.fillColor = COLORS.white
      cell.styles.textColor = COLORS.white
      cell.styles.fontStyle = 'bold'
      cell.styles.halign = 'center'
      cell.text = ['']
    },
    didDrawCell: ({ cell, column, section }) => {
      if (section !== 'body' || column.index !== tableHeaders.length - 1) return

      drawStatusPill(doc, cell.raw, cell)
    }
  })

  return doc.lastAutoTable.finalY
}

const drawEvidenceTitle = (doc, title, subtitle) => {
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(12)
  setTextColor(doc, COLORS.text)
  doc.text(title, PAGE_MARGIN_X, 46)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  setTextColor(doc, COLORS.muted)
  doc.text(subtitle, PAGE_MARGIN_X, 62)

  setDrawColor(doc, COLORS.softBorder)
  doc.setLineWidth(0.6)
  doc.line(PAGE_MARGIN_X, 76, getPageRight(doc), 76)
}

const drawEvidencePlaceholder = (doc, label = 'Adjunto no disponible', url = '') => {
  const pageWidth = getPageWidth(doc)
  const rectWidth = 420
  const rectHeight = 330
  const rectX = (pageWidth - rectWidth) / 2
  const rectY = 118

  setFillColor(doc, COLORS.softBg)
  doc.roundedRect(rectX, rectY, rectWidth, rectHeight, 5, 5, 'F')
  setDrawColor(doc, COLORS.border)
  doc.roundedRect(rectX, rectY, rectWidth, rectHeight, 5, 5, 'S')

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10)
  setTextColor(doc, COLORS.muted)
  doc.text(label, pageWidth / 2, rectY + rectHeight / 2, { align: 'center' })

  if (!url) return

  doc.setFontSize(9)
  setTextColor(doc, COLORS.primary)
  doc.textWithLink('Abrir adjunto original', pageWidth / 2, rectY + rectHeight / 2 + 20, {
    align: 'center',
    url
  })
}

const drawEvidenceImage = (doc, imageData) => {
  const maxWidth = getPageWidth(doc) - PAGE_MARGIN_X * 2 - 72
  const maxHeight = getPageHeight(doc) - 150
  const size = fitInside(imageData, maxWidth, maxHeight)
  const x = (getPageWidth(doc) - size.width) / 2
  const y = 92 + (maxHeight - size.height) / 2

  doc.addImage(imageData.dataUrl, imageData.format || 'JPEG', x, y, size.width, size.height)
}

const getAttachmentSubtitle = (attachment, number, suffix = '') => {
  const fallback = `Adjunto ${number}${suffix}`
  const label = getAttachmentLabel(attachment, fallback)

  return label === fallback ? fallback : `${fallback} - ${label}`
}

const addImageAttachmentPage = async (doc, attachment, number, options = {}) => {
  const url = attachment?.url
  const subtitle = getAttachmentSubtitle(attachment, number)

  doc.addPage()
  drawEvidenceTitle(doc, 'ADJUNTOS', subtitle)

  if (!url || !isFetchableUrl(url) || isUnavailableLocalUrl(url)) {
    drawEvidencePlaceholder(doc)
    return
  }

  try {
    const loader = options.loadImageDataUrl || loadImageDataUrl
    const imageData = await loader(url)
    drawEvidenceImage(doc, imageData)
  } catch (error) {
    console.error('Error al agregar imagen al PDF de devolución:', error)
    drawEvidencePlaceholder(doc)
  }
}

const addFileAttachmentPages = async (doc, attachment, number, options = {}) => {
  const url = attachment?.url
  const label = getAttachmentLabel(attachment, `Adjunto ${number}`)

  if (!url || !isFetchableUrl(url) || isUnavailableLocalUrl(url)) {
    doc.addPage()
    drawEvidenceTitle(doc, 'ADJUNTOS', getAttachmentSubtitle(attachment, number))
    drawEvidencePlaceholder(doc)
    return
  }

  if (isPdfUrl(url, label)) {
    try {
      const renderer = options.renderPdfToImageDataUrls || renderPdfToImageDataUrls
      const pages = await renderer(url)

      if (!pages.length) throw new Error('El adjunto PDF no contiene páginas renderizables')

      pages.forEach((page, pageIndex) => {
        doc.addPage()
        drawEvidenceTitle(
          doc,
          'ADJUNTOS',
          getAttachmentSubtitle(
            attachment,
            number,
            pages.length > 1 ? ` - Página ${pageIndex + 1}` : ''
          )
        )
        drawEvidenceImage(doc, page)
      })
      return
    } catch (error) {
      console.error('Error al renderizar PDF adjunto de devolución:', error)
    }
  }

  doc.addPage()
  drawEvidenceTitle(doc, 'ADJUNTOS', getAttachmentSubtitle(attachment, number))
  drawEvidencePlaceholder(doc, 'No se pudo incrustar este adjunto en el PDF', url)
}

const normalizeAttachments = ({ images = [], files = [] } = {}) => {
  const normalizedImages = normalizeAttachmentList(images)
  const normalizedFiles = normalizeAttachmentList(files)
  const imageFiles = normalizedFiles.filter(
    (file) => isImageUrl(file) || /^data:image\//i.test(file.url)
  )
  const mergedImages = [...normalizedImages, ...imageFiles].reduce((acc, item) => {
    if (!acc.some((current) => current.url === item.url)) acc.push(item)
    return acc
  }, [])
  const mergedFiles = normalizedFiles.filter(
    (file) => !mergedImages.some((image) => image.url === file.url)
  )

  return { images: mergedImages, files: mergedFiles }
}

const addAttachmentPages = async (doc, attachments, options = {}) => {
  const normalized = normalizeAttachments(attachments)

  if (normalized.images.length + normalized.files.length === 0) return

  let attachmentNumber = 1
  for (const image of normalized.images) {
    await addImageAttachmentPage(doc, image, attachmentNumber, options)
    attachmentNumber += 1
  }

  for (const file of normalized.files) {
    await addFileAttachmentPages(doc, file, attachmentNumber, options)
    attachmentNumber += 1
  }
}

export async function buildRefundApprovalPdf({
  activity,
  progress,
  summaryFields = [],
  extraFields = [],
  attachments = {},
  tableHeaders = [],
  tableRows = [],
  filenameBase,
  options = {}
}) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' })
  const generatedAt = new Date().toLocaleString()

  let startY = drawReportHeader(doc, activity)
  startY = drawSummaryCards(doc, progress, startY)
  startY = drawDetailsTable(doc, startY + 28, summaryFields, extraFields, activity)
  drawProductsTable(doc, startY + 28, tableHeaders, tableRows)

  await addAttachmentPages(doc, attachments, options)
  addPageFooters(doc, generatedAt)

  const fileRef = sanitizeFilenamePart(
    filenameBase || activity?.refund_db_ref || activity?.uuid,
    REFUND_APPROVAL_TEXT.filenameFallback
  )
  doc.save(`${fileRef || `productos-devolucion-${getCurrentDate()}`}.pdf`)
}
